import {EyeIcon, EyeOffIcon, Settings04Icon} from '@components/ui/icon/Icon.tsx'
import {useTranslation} from '@/translations/i18n.tsx'
import {VisibilityState} from '@tanstack/react-table'
import {TableButton} from '../table-button/TableButton'
import {
    DialogBody,
    DialogDisclosure,
    DialogDismiss,
    DialogFooter,
    DialogHeader,
    DialogHeading,
    DialogProvider
} from '@/components/ui/dialog-atoms/DialogAtoms'
import {Button} from '@/components/ui/button/Button'
import {StyledDialog, StyledVisibilityGroupButton, StyledVisibilityGroupRow} from './style'
import {ScrollArea} from '@/components/ui/scroll-area/ScrollArea'
import {Scrollbar} from '@/components/ui/scrollbar/Scrollbar'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Text} from '@/components/ui/text/Text'
import {Dispatch, SetStateAction, useState} from 'react'

type Column = {id: string; label: string}

interface ColumnVisibilityMenuProps<TColumn extends Column> {
    columns: TColumn[]
    columnVisibility: VisibilityState
    onSubmit: (columnVisibility: VisibilityState) => void
}
export const ColumnVisibilityMenu = <TColumn extends Column>({
    columns,
    columnVisibility,
    onSubmit
}: ColumnVisibilityMenuProps<TColumn>) => {
    const {t} = useTranslation()

    return (
        <DialogProvider>
            <DialogDisclosure render={<TableButton isActive={columns.some(column => !columnVisibility[column.id])} />}>
                <Settings04Icon />
                {t('commons:columns')}
            </DialogDisclosure>
            <StyledDialog>
                <Content columns={columns} columnVisibility={columnVisibility} onSubmit={onSubmit} />
            </StyledDialog>
        </DialogProvider>
    )
}

const Content = <TColumn extends Column>({
    columns,
    columnVisibility,
    onSubmit
}: {
    columns: TColumn[]
    columnVisibility: VisibilityState
    onSubmit: (columnVisibility: VisibilityState) => void
}) => {
    const {t} = useTranslation()
    const [localColumnVisibility, setLocalColumnVisibility] = useState<VisibilityState>(columnVisibility)

    const visibleColumns = columns.filter(column => localColumnVisibility[column.id])
    const hiddenColumns = columns.filter(column => !localColumnVisibility[column.id])
    return (
        <>
            <DialogHeader>
                <DialogHeading>{t('commons:columns')}</DialogHeading>
                <DialogDismiss />
            </DialogHeader>
            <ScrollArea scrollbar={<Scrollbar />}>
                <DialogBody>
                    {visibleColumns.length > 0 && (
                        <VisibilityGroup
                            type="visible"
                            columns={columns}
                            groupColumns={visibleColumns}
                            columnVisibility={localColumnVisibility}
                            setColumnVisibility={setLocalColumnVisibility}
                        />
                    )}
                    {hiddenColumns.length > 0 && (
                        <VisibilityGroup
                            type="hidden"
                            columns={columns}
                            groupColumns={hiddenColumns}
                            columnVisibility={localColumnVisibility}
                            setColumnVisibility={setLocalColumnVisibility}
                        />
                    )}
                </DialogBody>
            </ScrollArea>
            <DialogFooter style={{justifyContent: 'flex-end'}}>
                <DialogDismiss
                    render={
                        <Button variant="primaryNeutral" onClick={() => onSubmit(localColumnVisibility)}>
                            {t('commons:confirm')}
                        </Button>
                    }
                ></DialogDismiss>
            </DialogFooter>
        </>
    )
}

export const VisibilityGroup = <TColumn extends Column>({
    type,
    columns,
    groupColumns,
    columnVisibility,
    setColumnVisibility
}: {
    type: 'visible' | 'hidden'
    columns: TColumn[]
    groupColumns: TColumn[]
    columnVisibility: VisibilityState
    setColumnVisibility: Dispatch<SetStateAction<VisibilityState>>
}) => {
    const {t} = useTranslation()

    return (
        <div>
            <Flexbox justify="space-between" align="center">
                <Text weight="500" size="textMd">
                    {type === 'visible' ? t('commons:visible_columns') : t('commons:hidden_columns')}
                </Text>
                <StyledVisibilityGroupButton
                    variant="ghost"
                    shape="text"
                    onClick={() => {
                        const newVisibility = {...columnVisibility}
                        columns.forEach(column => {
                            newVisibility[column.id] = type === 'visible' ? false : true
                        })
                        setColumnVisibility(newVisibility)
                    }}
                >
                    {type === 'visible' ? t('commons:hide_all') : t('commons:show_all')}
                </StyledVisibilityGroupButton>
            </Flexbox>
            <div>
                {groupColumns.map(column => {
                    return (
                        <StyledVisibilityGroupRow key={column.id}>
                            <Text size="textMd">{column.label}</Text>
                            <Button
                                variant="ghost"
                                shape="text"
                                onClick={() => {
                                    setColumnVisibility(prev => ({
                                        ...prev,
                                        [column.id]: type === 'visible' ? false : true
                                    }))
                                }}
                            >
                                {type === 'visible' ? <EyeIcon size={16} /> : <EyeOffIcon size={16} />}
                            </Button>
                        </StyledVisibilityGroupRow>
                    )
                })}
            </div>
        </div>
    )
}
